import type { NumberSpeller } from './types'

const ONES = ['പൂജ്യം', 'ഒന്ന്', 'രണ്ട്', 'മൂന്ന്', 'നാല്', 'അഞ്ച്', 'ആറ്', 'ഏഴ്', 'എട്ട്', 'ഒമ്പത്', 'പത്ത്',
  'പതിനൊന്ന്', 'പന്ത്രണ്ട്', 'പതിമൂന്ന്', 'പതിനാല്', 'പതിനഞ്ച്', 'പതിനാറ്', 'പതിനേഴ്', 'പതിനെട്ട്', 'പത്തൊമ്പത്']
const TENS = ['', '', 'ഇരുപത്', 'മുപ്പത്', 'നാൽപ്പത്', 'അമ്പത്', 'അറുപത്', 'എഴുപത്', 'എൺപത്', 'തൊണ്ണൂറ്']
// Tens stem + unit fuse into one word: ഇരുപത്തി + യാറ് = ഇരുപത്തിയാറ് (26), + നാല് = ഇരുപത്തിനാല് (24).
const TENS_STEM = ['', '', 'ഇരുപത്തി', 'മുപ്പത്തി', 'നാൽപ്പത്തി', 'അമ്പത്തി', 'അറുപത്തി', 'എഴുപത്തി', 'എൺപത്തി', 'തൊണ്ണൂറ്റി']
const ONES_JOIN = ['', 'യൊന്ന്', 'രണ്ട്', 'മൂന്ന്', 'നാല്', 'യഞ്ച്', 'യാറ്', 'യേഴ്', 'യെട്ട്', 'യൊമ്പത്']
const HUNDREDS = ['', 'നൂറ്', 'ഇരുനൂറ്', 'മുന്നൂറ്', 'നാനൂറ്', 'അഞ്ഞൂറ്', 'അറുനൂറ്', 'എഴുനൂറ്', 'എണ്ണൂറ്', 'തൊള്ളായിരം']
const HUNDREDS_JOIN = ['', 'നൂറ്റി', 'ഇരുനൂറ്റി', 'മുന്നൂറ്റി', 'നാനൂറ്റി', 'അഞ്ഞൂറ്റി', 'അറുനൂറ്റി', 'എഴുനൂറ്റി', 'എണ്ണൂറ്റി',
  'തൊള്ളായിരത്തി']
// q × 1000 as one word: രണ്ടായിരം, മൂവായിരം, അയ്യായിരം, പതിനായിരം, ഇരുപതിനായിരം...
const THOUSANDS = ['', 'ആയിരം', 'രണ്ടായിരം', 'മൂവായിരം', 'നാലായിരം', 'അയ്യായിരം', 'ആറായിരം', 'ഏഴായിരം', 'എണ്ണായിരം',
  'ഒമ്പതിനായിരം', 'പതിനായിരം', 'പതിനൊന്നായിരം', 'പന്ത്രണ്ടായിരം', 'പതിമൂവായിരം', 'പതിനാലായിരം', 'പതിനയ്യായിരം',
  'പതിനാറായിരം', 'പതിനേഴായിരം', 'പതിനെണ്ണായിരം', 'പത്തൊമ്പതിനായിരം']
const TENS_THOUSANDS = ['', '', 'ഇരുപതിനായിരം', 'മുപ്പതിനായിരം', 'നാൽപ്പതിനായിരം', 'അമ്പതിനായിരം', 'അറുപതിനായിരം',
  'എഴുപതിനായിരം', 'എൺപതിനായിരം', 'തൊണ്ണൂറായിരം']
const THOUSANDS_JOIN = ['', 'യൊന്നായിരം', 'രണ്ടായിരം', 'മൂവായിരം', 'നാലായിരം', 'യയ്യായിരം', 'യാറായിരം', 'യേഴായിരം',
  'യെണ്ണായിരം', 'യൊമ്പതിനായിരം']

function below100(n: number): string {
  if (n < 20) return ONES[n]
  const t = Math.floor(n / 10)
  return n % 10 ? TENS_STEM[t] + ONES_JOIN[n % 10] : TENS[t]
}

function below1000(n: number): string {
  const h = Math.floor(n / 100)
  const r = n % 100
  if (!h) return below100(r)
  return r ? `${HUNDREDS_JOIN[h]} ${below100(r)}` : HUNDREDS[h]
}

function thousands(q: number): string {
  if (q < 20) return THOUSANDS[q]
  const t = Math.floor(q / 10)
  return q % 10 ? TENS_STEM[t] + THOUSANDS_JOIN[q % 10] : TENS_THOUSANDS[t]
}

/** ആയിരം → ആയിരത്തി, ലക്ഷം → ലക്ഷത്തി when more follows. */
function joining(word: string): string {
  return word.replace(/ം$/, 'ത്തി')
}

function spell(n: number): string {
  if (n === 0) return ONES[0]
  const parts: string[] = []
  const crore = Math.floor(n / 10_000_000)
  const lakh = Math.floor((n % 10_000_000) / 100_000)
  const thousand = Math.floor((n % 100_000) / 1000)
  const rest = n % 1000
  if (crore) parts.push(`${crore === 1 ? 'ഒരു' : spell(crore)} കോടി`)
  if (lakh) {
    const word = `${lakh === 1 ? 'ഒരു' : below100(lakh)} ലക്ഷം`
    parts.push(n % 100_000 ? joining(word) : word)
  }
  if (thousand) parts.push(rest ? joining(thousands(thousand)) : thousands(thousand))
  if (rest) parts.push(below1000(rest))
  return parts.join(' ')
}

export const ml: NumberSpeller = {
  spell,
  year: spell,
  rupees: 'രൂപ'
}
